import { Box } from "../../components/ui/box";
import {
  DrawerInsetPanel,
  DrawerPinned,
  DrawerPopup,
  useDrawerInsetPanel,
} from "../../components/ui/drawer";
import {
  POPOVER_POPUP_WIDTH,
  PopoverPopup,
  type PopoverPopupWidth,
} from "../../components/ui/popover";
import * as React from "react";
import { useEntitySelector } from "./entity-selector-context";
import { EntitySelectorEmpty } from "./entity-selector-empty";
import type { Omittable } from "../../lib/props";

const DEFAULT_MAX_HEIGHT = 400;

// THE picker rung: every selector popover that names no preset opens at this
// exact width, so the same picker is the same object wherever it opens — a
// FIXED width, not a floor, because a popup that resizes with its content
// reads as a different control each time (and an empty pool as a broken one).
// The number is the popover's own `picker` rung, so a list popup that is not
// an entity selector (an address lookup) opens at the same width by name.
const PICKER_WIDTH = POPOVER_POPUP_WIDTH.picker;

// Min-width presets for content the standard rung cannot hold — multi-pool
// tabs, selected-chip rows, long content lines. Explicit opt-ins only, and
// they are the popover's own width rungs, so a selector floored at `card`
// is as wide as the card popup beside it; the default is the fixed
// PICKER_WIDTH above (or the anchor floor under `fit`).
export type EntitySelectorContentSize = Exclude<PopoverPopupWidth, "picker">;

/** A width capped to the viewport minus a 1rem gutter per side. */
function cappedToViewport(width: number | string): string {
  const value = typeof width === "number" ? `${width}px` : width;
  return `min(${value}, calc(100vw - 2rem))`;
}

// The inline rung adds two percentage bounds. While a host that sizes to its
// content (a submenu popup, a suggestion panel) measures that content, a
// percentage in min-/max-width counts for nothing, so such a host still lands
// on the fixed picker rung. A host that is ALREADY sized — a bar's pinned
// popover, a dialog body, a drawer panel — resolves them against its own
// width, and the content spans it. Without the bounds the list sat as a 280px
// strip down such a host's left edge: search well and scrollbar stopping
// short of the frame, every row truncating against empty space.
const INLINE_PICKER_WIDTH_STYLE: React.CSSProperties = {
  width: cappedToViewport(PICKER_WIDTH),
  minWidth: "100%",
  maxWidth: "100%",
};

// Drawer mode pins the search input (and pool tabs, and a step's title row)
// above the one scrollable panel — a scrolling search field on a touch drawer is unusable. The split is
// declarative: these parts render in the fixed header region, everything else
// (selected rows, pools, footer) scrolls inside the DrawerPanel.
const DRAWER_HEADER_DISPLAY_NAMES = new Set([
  "EntitySelectorStepHeader",
  "EntitySelectorInput",
  "EntitySelectorTabs",
  "EntitySelectorTabsList",
  "EntitySelectorAutoTabs",
]);

// Every selector gets an empty state whether or not the call site remembered to
// render one — a popup showing a search field above nothing at all is the
// failure this replaces, and it is the default a consumer never opts into. An
// explicit `EntitySelectorEmpty` anywhere in the children wins; the fallback
// only fills a gap.
//
// "Anywhere" means through ANY wrapper, not just a Fragment. Nearly every call
// site wraps its parts in a `Box` for the max-height/overflow column, so a scan
// that only descended into Fragments missed the explicit empty state and
// appended the fallback beside it — the popup then showed BOTH messages ("No
// events found" over "No results found").
//
// The limit worth knowing: this walks the element tree the consumer passed as
// JSX. An `EntitySelectorEmpty` returned from inside a consumer's OWN component
// is not visible here, so render it in this tree rather than hiding it behind a
// wrapper component.
function hasEmptyChild(children: React.ReactNode): boolean {
  let found = false;
  React.Children.forEach(children, (child) => {
    if (found || !React.isValidElement(child)) return;
    if (displayNameOf(child) === "EntitySelectorEmpty") {
      found = true;
      return;
    }
    const nested = (child.props as { children?: React.ReactNode }).children;
    if (nested != null) found = hasEmptyChild(nested);
  });
  return found;
}

function displayNameOf(child: React.ReactElement): string | undefined {
  return (child.type as { displayName?: string })?.displayName;
}

function withEmptyState(children: React.ReactNode): React.ReactNode {
  if (hasEmptyChild(children)) return children;
  const list = React.Children.toArray(children);
  // Ahead of the footer, which is the popup's last row and sits below the list.
  const footerIndex = list.findIndex(
    (child) =>
      React.isValidElement(child) &&
      displayNameOf(child) === "EntitySelectorFooter",
  );
  const fallback = <EntitySelectorEmpty key="entity-selector-empty" />;
  return footerIndex === -1
    ? [...list, fallback]
    : [...list.slice(0, footerIndex), fallback, ...list.slice(footerIndex)];
}

function partitionDrawerChildren(children: React.ReactNode): {
  header: React.ReactNode[];
  panel: React.ReactNode[];
} {
  const header: React.ReactNode[] = [];
  const panel: React.ReactNode[] = [];
  React.Children.forEach(children, (child) => {
    const displayName = React.isValidElement(child)
      ? displayNameOf(child)
      : undefined;
    if (displayName && DRAWER_HEADER_DISPLAY_NAMES.has(displayName)) {
      header.push(child);
    } else {
      panel.push(child);
    }
  });
  return { header, panel };
}

export type EntitySelectorContentProps = Omittable<{
  side?: "top" | "bottom";
  align?: "start" | "center" | "end";
  maxHeight?: number;
  /** Maximum width of the content area in pixels. Omit to let the popover (or `fit`/`size` preset) drive width. */
  maxWidth?: number;
  /** Minimum height for the content area. Prevents the popover from collapsing when results are empty. */
  minHeight?: number;
  /** Minimum width preset for content the standard fixed rung cannot hold —
   *  the popover's own `card` / `builder` / `panel` rungs, as a floor. Omit
   *  for the default: the fixed picker width. */
  size?: EntitySelectorContentSize;
  /**
   * Floors the content at the trigger's width, for a picker standing in a
   * form as a full-width field. It is a MINIMUM, not a match, and never
   * below the picker rung: a narrow trigger (a chip, a property row's value)
   * still gets the standard picker, so `fit` only ever widens.
   * @default false
   */
  fit?: boolean;
  /**
   * Key handling for a list with NO search field. The rows are not tabbable
   * (the search field is the list's keyboard home), so a list that drops the
   * field seats focus on the popup and hears the arrows and Enter here —
   * spread `useSelectorKeyboardNav().inputProps` on. Pair with
   * `initialFocus` returning the popup, or the open lands on the footer's
   * first tabbable instead.
   */
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
  /** Where the popover or drawer seats focus on open. Omit for the default:
   *  the first tabbable on a fine pointer, the popup itself on touch. */
  initialFocus?: React.ComponentProps<typeof PopoverPopup>["initialFocus"];
  /** The drawer's caption on a phone: a small centred name for what the
   *  list is ("Assignees"), under the grab bar and above the pinned search.
   *  The popover shows nothing — its trigger is right there — and an inline
   *  mount leaves the caption to its sheet host (`sheetTitle`). */
  title?: React.ReactNode;
  ref?: React.Ref<HTMLDivElement>;
  children: React.ReactNode;
}>;

function EntitySelectorContent({
  side = "bottom",
  align = "start",
  maxHeight = DEFAULT_MAX_HEIGHT,
  maxWidth,
  minHeight,
  size,
  fit = false,
  onKeyDown,
  initialFocus,
  title: titleProp,
  ref,
  children,
}: EntitySelectorContentProps) {
  const { variant, title: rootTitle } = useEntitySelector();
  const title = titleProp ?? rootTitle;
  // An inline mount inside a phone sheet's body (a responsive popover's, a
  // submenu sheet's, a table cell's): the sheet's panel is the ONE scroller,
  // so the content takes no height cap of its own — see the pool's flat rows.
  const inSheet = useDrawerInsetPanel();

  const body = withEmptyState(children);

  // The height cap also yields to the popup's available height (set by the
  // hosting positioner — this popover's, or the menu or popover an inline
  // mount sits in), so the list's ONE scroller is always the pool's: a column
  // taller than the popup would make the popup's viewport a second scroller
  // that carries the search field away.
  // `--available-height` is registered as a <length> in index.css: the
  // keyword Base UI parks there while it measures the popup's natural size
  // would otherwise void this `min()`, and the list would measure unclipped.
  const constraintStyle = React.useMemo(
    () => ({
      maxHeight: `min(${maxHeight}px, var(--available-height, ${maxHeight}px))`,
      ...(maxWidth != null && !fit && { maxWidth }),
      ...(minHeight != null && { minHeight }),
    }),
    [maxHeight, maxWidth, minHeight, fit],
  );

  // A named preset floors the width at that rung; `fit` floors it at the
  // anchor, never below the picker rung. Both are viewport-capped. Pixels
  // through `style`, the way PopoverPopup's own `width` rung travels: the
  // popup's base width is a Base UI variable, and a class would race it.
  const floorStyle = React.useMemo<React.CSSProperties | undefined>(() => {
    if (size) return { minWidth: cappedToViewport(POPOVER_POPUP_WIDTH[size]) };
    if (fit) {
      return {
        minWidth: cappedToViewport(`max(${PICKER_WIDTH}px, var(--anchor-width))`),
      };
    }
    return undefined;
  }, [size, fit]);

  // Inline mode: no popover/drawer wrapper — the host (a submenu popup, a
  // panel) supplies the chrome. The content still owns its own width and
  // height cap; without them the host's tiny min-width (a menu's 128px) sizes
  // the whole selector and the pool overflows the host instead of scrolling.
  //
  // Inside a phone sheet's body the same split the drawer makes applies: the
  // search well, tabs and a step's title row pin above the sheet's one
  // scroller (portalled into its band), the rest scrolls with it.
  if (variant === "inline") {
    const { header, panel } = inSheet
      ? partitionDrawerChildren(body)
      : { header: [], panel: [body] };
    return (
      <Box
        direction="column"
        onKeyDown={onKeyDown}
        overflow={inSheet ? undefined : "hidden"}
        // The same fixed picker rung the popover branch opens at — an inline
        // mount is the same picker inside a host-owned popup, and a host that
        // sizes to content lands on the standard width for free — spanning a
        // host that is already sized (see INLINE_PICKER_WIDTH_STYLE). A named
        // `size` preset floors it wider instead. A caller's own maxWidth
        // still wins over the span.
        style={{
          ...(floorStyle ?? INLINE_PICKER_WIDTH_STYLE),
          ...(inSheet ? undefined : constraintStyle),
        }}
      >
        {header.length > 0 && <DrawerPinned>{header}</DrawerPinned>}
        {panel}
      </Box>
    );
  }

  // Drawer mode: the sheet body (`DrawerInsetPanel`) pins the caption, the
  // search input and tabs above the DrawerPanel — the panel's ScrollArea is
  // the ONE scrollable, so pools render their rows flat inside it (no nested
  // scroller).
  //
  // The list's own 8px gutter was tuned for a 280px popover hung off a chip;
  // against a phone's bezel it runs the row wash to the screen edge while
  // every other bottom sheet breathes. The sheet body adds its 16px inset
  // around the list (the rows carry 10px of edge of their own, so the
  // drawer's full 24px body inset would read as a gutter). The footer breaks
  // back out of it so its hairline spans the sheet — see
  // `EntitySelectorFooter`.
  if (variant === "drawer") {
    const { header, panel } = partitionDrawerChildren(body);
    return (
      <DrawerPopup initialFocus={initialFocus} ref={ref} showBar>
        <DrawerInsetPanel
          header={header.length > 0 ? header : undefined}
          title={title}
        >
          {panel}
        </DrawerInsetPanel>
      </DrawerPopup>
    );
  }

  // Desktop: render popover with consumer-provided children. The selector list
  // owns its padding and scroll, so the popup carries none.
  return (
    <PopoverPopup
      ref={ref}
      side={side}
      align={align}
      initialFocus={initialFocus}
      onKeyDown={onKeyDown}
      padding="none"
      // A named preset or `fit` floors the popup (floorStyle); otherwise the
      // popup is the fixed picker rung, by name. The popup caps itself at the
      // positioner's available width, so the rung never runs off a phone.
      width={floorStyle ? undefined : "picker"}
      style={floorStyle}
    >
      <Box fill="width"
        direction="column"
        overflow="hidden"
        style={constraintStyle}
      >
        {body}
      </Box>
    </PopoverPopup>
  );
}

EntitySelectorContent.displayName = "EntitySelectorContent";

export { EntitySelectorContent };
